import React, { useState, useEffect } from "react";
import { MessageCircle, Phone, X } from "lucide-react";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { useToast } from "../hooks/use-toast";

const FloatingButtons = () => {
  const { toast } = useToast();
  const [isCallbackModalOpen, setIsCallbackModalOpen] = useState(false);
  const [isVisible, setIsVisible] = useState(false);
  const [callbackData, setCallbackData] = useState({
    name: "",
    phone: "",
    message: "",
  });

  useEffect(() => {
    const handleScroll = () => {
      setIsVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleWhatsAppClick = () => {
    const phoneNumber = "33663408699";
    const message = encodeURIComponent("Bonjour, je souhaite obtenir plus d'informations sur vos solutions énergétiques.");
    window.open(`https://wa.me/${phoneNumber}?text=${message}`, "_blank");
  };

  const handleCallbackSubmit = async (e) => {
    e.preventDefault();
    try {
      const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${BACKEND_URL}/api/submissions/callback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(callbackData),
      });

      if (response.ok) {
        toast({
          title: "Demande de rappel envoyée !",
          description: "Nous vous rappellerons dans les plus brefs délais.",
        });
        setCallbackData({ name: "", phone: "", message: "" });
        setIsCallbackModalOpen(false);
      } else {
        throw new Error('Erreur lors de l\'envoi');
      }
    } catch (error) {
      console.error('Error submitting callback:', error);
      toast({
        title: "Erreur",
        description: "Une erreur est survenue. Veuillez réessayer.",
        variant: "destructive",
      });
    }
  };

  return (
    <>
      {/* WhatsApp Button */}
      <div
        className={`fixed bottom-6 right-6 z-50 transition-all duration-300 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"
        }`}
      >
        <button
          onClick={handleWhatsAppClick}
          className="group w-16 h-16 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 relative"
          aria-label="Contacter sur WhatsApp"
        >
          <MessageCircle size={28} />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></span>
          
          {/* Tooltip */}
          <span className="absolute right-full mr-3 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Contactez-nous sur WhatsApp
          </span>
        </button>
      </div>

      {/* Call Me Back Button */}
      <div
        className={`fixed bottom-6 left-6 z-50 transition-all duration-300 ${
          isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10 pointer-events-none"
        }`}
      >
        <button
          onClick={() => setIsCallbackModalOpen(true)}
          className="group w-16 h-16 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white rounded-full shadow-2xl flex items-center justify-center transition-all hover:scale-110 relative"
          aria-label="Être rappelé"
        >
          <Phone size={28} />
          <span className="absolute -top-1 -right-1 w-4 h-4 bg-red-500 rounded-full animate-pulse"></span>
          
          {/* Tooltip */}
          <span className="absolute left-full ml-3 px-4 py-2 bg-gray-900 text-white text-sm rounded-lg whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none">
            Être rappelé gratuitement
          </span>
        </button>
      </div>

      {/* Callback Modal */}
      <Dialog open={isCallbackModalOpen} onOpenChange={setIsCallbackModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="text-2xl font-bold text-gray-900">
              Demande de rappel gratuit
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleCallbackSubmit} className="space-y-4 mt-4">
            <div>
              <label htmlFor="callback-name" className="block text-sm font-semibold text-gray-900 mb-2">
                Nom complet *
              </label>
              <Input
                id="callback-name"
                type="text"
                required
                value={callbackData.name}
                onChange={(e) => setCallbackData({ ...callbackData, name: e.target.value })}
                placeholder="Votre nom"
              />
            </div>
            <div>
              <label htmlFor="callback-phone" className="block text-sm font-semibold text-gray-900 mb-2">
                Téléphone *
              </label>
              <Input
                id="callback-phone"
                type="tel"
                required
                value={callbackData.phone}
                onChange={(e) => setCallbackData({ ...callbackData, phone: e.target.value })}
                placeholder="06 00 00 00 00"
              />
            </div>
            <div>
              <label htmlFor="callback-message" className="block text-sm font-semibold text-gray-900 mb-2">
                Message (optionnel)
              </label>
              <Textarea
                id="callback-message"
                value={callbackData.message}
                onChange={(e) => setCallbackData({ ...callbackData, message: e.target.value })}
                placeholder="Précisez votre demande..."
                className="min-h-[100px]"
              />
            </div>
            <div className="flex space-x-3">
              <Button
                type="button"
                variant="outline"
                onClick={() => setIsCallbackModalOpen(false)}
                className="flex-1"
              >
                Annuler
              </Button>
              <Button
                type="submit"
                className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white border-0"
              >
                Envoyer
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default FloatingButtons;